/* eslint-disable @typescript-eslint/no-explicit-any */
import EditIcon from '@mui/icons-material/Edit';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import { UniversalDateRangePicker } from 'components/enhanced/data-grid/components/date-picker/UniversalDateRangePicker';
import { calculateFulfilledIdsInWindows } from 'components/helpers/dateWindows';
import { ActionMenu } from 'components/ui/ActionMenu';
import { ClearFiltersButton } from 'components/ui/buttons/ClearFiltersButton';
import { CreateButton } from 'components/ui/buttons/CreateButton';
import { equipmentCheckListTemplateService } from 'features/inventory/checklist/api/checklist-template.service';
import { equipmentCheckListService } from 'features/inventory/checklist/api/checklist.service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Option } from 'shared/types/general';
import { ChecklistCompileDialog } from './dialogs/EditChecklistDialog';

interface FrequencySection {
  key: string;
  label: string;
}

export const DailyEquipmentChecklistsTab = ({ equipment }) => {
  const navigate = useNavigate();
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  // const todayStr = '2026-08-01';

  const todayFormattedHeader = useMemo(() => {
    const [year, month, day] = todayStr.split('-');
    return `${day}/${month}/${year.slice(-2)}`;
  }, [todayStr]);

  const [unifiedRows, setUnifiedRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCompileDialogOpen, setIsCompileDialogOpen] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenuRow, setActiveMenuRow] = useState<any>(null);

  const [dateStart, setDateStart] = useState<string>('');
  const [dateEnd, setDateEnd] = useState<string>('');

  const [frequencyTypes, setFrequencyTypes] = useState<Option[]>([]);
  const [sections, setSections] = useState<FrequencySection[]>([]);

  useEffect(() => {
    loadTypeOptions();
  }, []);

  const loadTypeOptions = () => {
    equipmentCheckListTemplateService.options().then((res) => {
      const choices = res.actions?.POST?.frequency?.choices || [];

      setFrequencyTypes(
        choices.map((choice: any) => ({
          id: choice.value,
          code: choice.display_name
        }))
      );

      setSections(
        choices.map((choice: any) => ({
          key: choice.value,
          label: choice.display_name
        }))
      );
    });
  };

  const fetchAndMergeUnifiedData = useCallback(async () => {
    if (!equipment?.id) return [];
    setLoading(true);
    try {
      const [realChecklistsRes, templatesRes] = await Promise.all([
        equipmentCheckListService.getAllPaginated(0, 0),
        equipmentCheckListTemplateService.getAllPaginated(0, 0)
      ]);

      const realListRaw = Array.isArray(realChecklistsRes)
        ? realChecklistsRes
        : realChecklistsRes?.results || [];
      const templateListRaw = Array.isArray(templatesRes)
        ? templatesRes
        : templatesRes?.results || [];

      const realList = realListRaw.filter((cl: any) => cl.equipment === equipment?.id);

      const templateList = templateListRaw.filter((tmpl: any) => {
        if (Array.isArray(tmpl.equipments)) {
          return tmpl.equipments.some(
            (eq: any) => (typeof eq === 'object' ? eq.id : eq) === equipment.id
          );
        }
        const eqId =
          tmpl.equipment && typeof tmpl.equipment === 'object' ? tmpl.equipment.id : tmpl.equipment;
        return eqId === equipment.id;
      });

      const templatesEvasitiNelPeriodoMap = calculateFulfilledIdsInWindows(realList, todayStr, {
        getId: (cl: any) => cl.template,
        getFrequency: (cl: any) => cl.frequency || cl.template_details?.frequency,
        getDate: (cl: any) => cl.date || cl.created_at
      });

      const phantomChecklists = templateList
        .filter((tmpl: any) => tmpl.is_active && !templatesEvasitiNelPeriodoMap.has(tmpl.id))
        .map((tmpl: any, index: number) => ({
          id: `phantom-${tmpl.id}-${index}-${Date.now()}`,
          templateId: tmpl.id,
          equipment: tmpl.equipment,
          name: tmpl.name,
          frequency: tmpl.frequency,
          description: tmpl.description || '',
          notes: tmpl.notes || '',
          date: todayStr,
          sections: tmpl.sections || [],
          progress: 0,
          is_phantom: true
        }));

      const sortedRealList = [...realList].sort((a: any, b: any) => b.id - a.id);
      const unified = [...phantomChecklists, ...sortedRealList];

      setUnifiedRows(unified);
      return unified;
    } catch (err) {
      console.error('Errore fetch dati custom:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [equipment?.id, todayStr]);

  useEffect(() => {
    fetchAndMergeUnifiedData();
  }, [fetchAndMergeUnifiedData]);

  const getFilteredDataByFrequency = useCallback(
    (frequencyKey: string) => {
      const hasActiveFilters = !!(dateStart || dateEnd);
      let processed = unifiedRows;

      if (hasActiveFilters) {
        processed = processed.filter((row) => {
          if (row.is_phantom) {
            const todayTime = new Date(todayStr).getTime();
            if (dateStart && todayTime < new Date(dateStart).getTime()) return false;
            if (dateEnd && todayTime > new Date(dateEnd).getTime()) return false;
            return true;
          }
          const rowDateStr = (row.created_at || row.date || '').split('T')[0];
          if (rowDateStr) {
            const rowTime = new Date(rowDateStr).getTime();
            if (dateStart && rowTime < new Date(dateStart).getTime()) return false;
            if (dateEnd && rowTime > new Date(dateEnd).getTime()) return false;
          }
          return true;
        });
      } else {
        processed = processed.filter((row) => {
          if (row.is_phantom) return true;
          const rowDateStr = (row.date || row.created_at || '').split('T')[0];
          return rowDateStr === todayStr;
        });
      }

      return processed.filter((row) => {
        const rowFrequency = row.frequency || row.template_details?.frequency;
        return rowFrequency === frequencyKey;
      });
    },
    [unifiedRows, dateStart, dateEnd, todayStr]
  );

  const handleCompilationFormSaveSuccess = async (savedChecklist: any) => {
    setIsCompileDialogOpen(false);
    setSelectedRowData(null);

    if (savedChecklist && savedChecklist.template) {
      setUnifiedRows((prevRows) => {
        const targetTemplateId = savedChecklist.template;
        const filteredPhantom = prevRows.filter(
          (row) => !row.is_phantom || row.templateId !== targetTemplateId
        );
        const updatedReal = [savedChecklist, ...filteredPhantom.filter((r) => !r.is_phantom)];
        const phantoms = filteredPhantom.filter((r) => r.is_phantom);
        return [...phantoms, ...updatedReal];
      });
    }
    setTimeout(() => {
      fetchAndMergeUnifiedData();
    }, 300);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setActiveMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenuRow(null);
  };

  const menuItems = useMemo(() => {
    if (!activeMenuRow) return [];

    return [
      {
        id: 'duplicate',
        label: 'Duplica',
        icon: <LibraryBooksIcon fontSize="small" />,
        onClick: () => {
          navigate(`/tenant/equipments/checklist/new/`, {
            state: { duplicateData: activeMenuRow, isDuplicate: true }
          });
        }
      },
      {
        id: 'compile',
        label: 'Compila',
        icon: <EditIcon fontSize="small" />,
        onClick: () => {
          setSelectedRowData(activeMenuRow);
          setIsCompileDialogOpen(true);
        }
      }
    ];
  }, [activeMenuRow, navigate]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, p: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          px: 0.5,
          width: '100%'
        }}>
        {/* TOOLBAR */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* FILTERS */}
          <UniversalDateRangePicker
            value={[dateStart || null, dateEnd || null]}
            onChange={(range) => {
              if (!range) {
                setDateStart('');
                setDateEnd('');
              } else {
                setDateStart(range[0] || '');
                setDateEnd(range[1] || '');
              }
            }}
          />

          {(dateStart || dateEnd) && (
            <ClearFiltersButton
              onClear={() => {
                setDateStart('');
                setDateEnd('');
              }}
            />
          )}
        </Box>

        {/* ACTIONS */}
        <CreateButton
          handleClick={() => {
            navigate(`/tenant/equipments/checklist/new/`, {
              state: {
                equipmentId: equipment?.id,
                equipmentCode: equipment?.code,
                equipmentDescription: equipment?.description
              }
            });
          }}
          label=" Nuova Checklist"
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading &&
        sections.map((section) => {
          const rowsForSection = getFilteredDataByFrequency(section.key);

          const totalRows = rowsForSection.length;
          const completedRows = rowsForSection.filter(
            (row) =>
              !row.is_phantom && (typeof row.progress === 'number' ? row.progress : 0) === 100
          ).length;

          const counterLabel = totalRows > 0 ? `${completedRows} su ${totalRows}` : '';

          return (
            <Box key={section.key} sx={{ width: '100%', mb: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  px: 0.5,
                  width: '100%'
                }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.88rem',
                    fontFamily: 'sans-serif'
                  }}>
                  {section.label}
                </Typography>

                {totalRows > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.88rem',
                      fontFamily: 'sans-serif',
                      px: 1.2,
                      py: 0.3
                    }}>
                    {counterLabel}
                  </Typography>
                )}
              </Box>

              <Box sx={{ width: '100%', borderTop: '1px solid #f0f0f0' }}>
                {rowsForSection.length === 0 ? (
                  <Box sx={{ py: 2.5, px: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#b5b5b5', fontSize: '0.85rem', fontStyle: 'italic' }}>
                      Nessuna attività pianificata o registrata
                    </Typography>
                  </Box>
                ) : (
                  rowsForSection.map((row) => {
                    const isPhantom = !!row.is_phantom;

                    const currentProgress = typeof row.progress === 'number' ? row.progress : 0;

                    let statusLabel = 'Completata';
                    let stateColors = {
                      rowBg: 'rgba(232, 245, 233, 0.4)',
                      rowBgHover: 'rgba(232, 245, 233, 0.7)',
                      borderLeft: '#4caf50', //
                      iconBg: '#e8f5e9',
                      iconColor: '#2e7d32',
                      chipBg: '#e8f5e9',
                      chipText: '#2e7d32',
                      textMain: '#1b5e20',
                      textMuted: '#4caf50'
                    };

                    if (isPhantom && currentProgress === 0) {
                      statusLabel = 'Da compilare';
                      stateColors = {
                        rowBg: '#ffffff',
                        rowBgHover: '#f5f9ff',
                        borderLeft: '#b0bec5',
                        iconBg: '#e3f2fd',
                        iconColor: '#1e88e5',
                        chipBg: '#f5f5f5',
                        chipText: '#616161',
                        textMain: '#1a1a1a',
                        textMuted: '#888888'
                      };
                    } else if (currentProgress > 0 && currentProgress < 100) {
                      statusLabel = `In corso (${currentProgress}%)`;
                      stateColors = {
                        rowBg: 'rgba(255, 248, 225, 0.5)',
                        rowBgHover: 'rgba(255, 248, 225, 0.8)',
                        borderLeft: '#ffb300',
                        iconBg: '#fff3e0',
                        iconColor: '#f57c00',
                        chipBg: '#fff3e0',
                        chipText: '#e65100',
                        textMain: '#e65100',
                        textMuted: '#f57c00'
                      };
                    }

                    const operatorName =
                      row.created_by_detail?.full_name || row.created_by_detail?.username || '';

                    const rawDateStr = (row.date || row.created_at || todayStr).split('T')[0];
                    const [y, m, d] = rawDateStr.split('-');
                    const formattedRowDate =
                      y && m && d ? `${d}/${m}/${y.slice(-2)}` : todayFormattedHeader;

                    return (
                      <Box
                        key={row.id}
                        onClick={() => {
                          setSelectedRowData(row);
                          setIsCompileDialogOpen(true);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1,
                          px: 1,
                          borderBottom: '1px solid #f2f2f2',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          '&:hover': { backgroundColor: '#f5f9ff' }
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 30,
                              height: 30,
                              borderRadius: '6px',
                              backgroundColor: stateColors.iconBg,
                              color: stateColors.iconColor,
                              transition: 'all 0.15s ease'
                            }}>
                            <FormatListBulletedIcon sx={{ fontSize: '1.05rem' }} />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: '#1a1a1a',
                              fontSize: '0.85rem',
                              minWidth: 32
                            }}>
                            {row.name}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{ color: '#888888', fontSize: '0.85rem', minWidth: 65 }}>
                            {isPhantom ? '' : formattedRowDate}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: '#888888',
                              fontSize: '0.8rem',
                              lineHeight: 1.2,
                              textTransform: 'capitalize'
                            }}>
                            {isPhantom && currentProgress === 0 ? '' : operatorName}
                          </Typography>

                          <Chip
                            label={statusLabel}
                            size="small"
                            sx={{
                              borderRadius: '12px',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              height: 22,
                              px: 0.5,
                              backgroundColor: stateColors.chipBg,
                              color: stateColors.chipText,
                              border: 'none'
                            }}
                          />

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, row);
                            }}
                            sx={{ color: '#9e9e9e', p: 0.2, '&:hover': { color: '#616161' } }}
                            aria-label="opzioni riga">
                            <MoreVertIcon sx={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          );
        })}

      <ActionMenu anchorEl={anchorEl} onClose={handleMenuClose} items={menuItems} />

      {isCompileDialogOpen && (
        <ChecklistCompileDialog
          open={isCompileDialogOpen}
          rowData={selectedRowData}
          equipmentId={equipment.id}
          onClose={() => setIsCompileDialogOpen(false)}
          onSaveSuccess={handleCompilationFormSaveSuccess}
        />
      )}
    </Box>
  );
};
